import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import {
  createTrackRight,
  deleteTrackRight,
  getKakaoAccountIdLinkedTracks,
  linkedTracks,
  unlinkedTracks,
} from 'api/axios/crux/bcon'
import api from 'api/axios/foka'
import { serializeError } from 'utils'

const initialState = {
  tab: 'linked',
  linkedContent: [],
  linkedPage: 0,
  linkedTotalElements: 0,
  linkedLoading: false,
  linkedLoadingError: null,
  unlinkedContent: [],
  unlinkedPage: 0,
  unlinkedTotalElements: 0,
  unlinkedLoading: false,
  unlinkedLoadingError: null,
  kakaoAccountIdLinkedCount: 0,
}

export const fetchTracks = createAxiosThunk(
  'trackerList/fetchTracks',
  async (noArgs, { getState }) => {
    const adAccountId = getState().account.current.id
    const {
      data: { body: linked },
    } = await linkedTracks({
      page: 0,
      adAccountId,
      size: 1,
    })
    const {
      data: { body: unlinked },
    } = await unlinkedTracks({
      page: 0,
      adAccountId,
      size: 1,
    })
    return api.response({
      linked,
      unlinked,
    })
  }
)

export const fetchLinkedTracks = createAxiosThunk(
  'trackerList/fetchLinkedTracks',
  async (noArgs, { getState }) => {
    const page = getState().tracker.trackerList.linkedPage
    const adAccountId = getState().account.current.id
    const kakaoAccountIdLinkedCount = await getKakaoAccountIdLinkedTracks()
      .then(res => res.data?.body?.totalElements || 0)
      .catch(() => 0)
    const { data } = await linkedTracks({
      page,
      adAccountId,
    })
    return api.response({
      ...data,
      kakaoAccountIdLinkedCount,
    })
  }
)

export const fetchUnlinkedTracks = createAxiosThunk(
  'trackerList/fetchUnlinkedTracks',
  async (noArgs, { getState }) => {
    const page = getState().tracker.trackerList.unlinkedPage
    const adAccountId = getState().account.current.id
    const kakaoAccountIdLinkedCount = await getKakaoAccountIdLinkedTracks()
      .then(res => res.data?.body?.totalElements || 0)
      .catch(() => 0)
    const { data } = await unlinkedTracks({
      page,
      adAccountId,
    })
    return api.response({
      ...data,
      kakaoAccountIdLinkedCount,
    })
  }
)

export const linkTrackRight = createAxiosThunk(
  'trackerList/linkTrackRight',
  (trackId, { getState }) => {
    const adAccountId = getState().account.current.id
    return createTrackRight({
      trackId,
      adAccountId,
    })
  }
)

export const unlinkTrackRight = createAsyncThunk(
  'trackerList/unlinkTrackRight',
  async (trackId, { getState, rejectWithValue }) => {
    const adAccountId = getState().account.current.id
    try {
      const { data } = await deleteTrackRight({
        trackId,
        adAccountId,
      })
      return data
    } catch (error) {
      if (error && error.response && error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(serializeError(error))
      }
    }
  }
)

const trackerListSlice = createSlice({
  name: 'trackerList',
  initialState,

  reducers: {
    reset: state => ({
      ...initialState,
      requestTracks: state.requestTracks,
    }),
    setTab: (state, { payload }) => {
      state.tab = payload
      if (payload === 'linked') {
        state.linkedPage = 0
      } else {
        state.unlinkedPage = 0
      }
    },
    setLinkedPage: (state, { payload: page }) => {
      state.linkedPage = page
    },
    setUnlinkedPage: (state, { payload: page }) => {
      state.unlinkedPage = page
    },
    showCreateModal: state => {
      state.createModalVisible = true
    },
    hideCreateModal: state => {
      state.createModalVisible = false
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchLinkedTracks.pending, state => {
      state.linkedLoadingError = null
      state.linkedLoading = true
    })

    builder.addCase(
      fetchLinkedTracks.fulfilled,
      (state, { payload: { body, kakaoAccountIdLinkedCount } }) => {
        const { content, totalElements, numberOfElements, totalPages } = body
        if (numberOfElements === 0 && totalElements > 0) {
          state.linkedPage = totalPages - 1
        } else {
          state.linkedContent = content
          state.linkedTotalElements = totalElements
        }
        state.kakaoAccountIdLinkedCount = kakaoAccountIdLinkedCount
        state.linkedLoading = false
      }
    )

    builder.addCase(fetchLinkedTracks.rejected, (state, { payload }) => {
      state.linkedLoadingError = payload.errorCode || true
      state.linkedContent = []
      state.linkedLoading = false
    })

    builder.addCase(fetchUnlinkedTracks.pending, state => {
      state.unlinkedLoadingError = null
      state.unlinkedLoading = true
    })

    builder.addCase(
      fetchUnlinkedTracks.fulfilled,
      (state, { payload: { body = {}, kakaoAccountIdLinkedCount } }) => {
        const { content, totalElements, numberOfElements, totalPages } = body
        if (numberOfElements === 0 && totalElements > 0) {
          state.unlinkedPage = totalPages - 1
        } else {
          state.unlinkedContent = content
          state.unlinkedTotalElements = totalElements
        }
        state.kakaoAccountIdLinkedCount = kakaoAccountIdLinkedCount
        state.unlinkedLoading = false
      }
    )

    builder.addCase(fetchUnlinkedTracks.rejected, (state, { payload }) => {
      state.unlinkedLoadingError = payload.errorCode || true
      state.unlinkedContent = []
      state.unlinkedLoading = false
    })

    builder.addCase(
      fetchTracks.fulfilled,
      (state, { payload: { linked, unlinked } }) => {
        if (linked.totalElements) {
          state.linkedTotalElements = linked.totalElements
        }
        if (unlinked.totalElements) {
          state.unlinkedTotalElements = unlinked.totalElements
        }
        if (linked?.totalElements === 0 && unlinked?.totalElements > 0) {
          state.tab = 'unlinked'
        }
      }
    )
  },
})

export const { setLinkedPage, setUnlinkedPage, setTab, reset } =
  trackerListSlice.actions
export default trackerListSlice.reducer
